"use client";

import Image from "next/image";
import Link from "@/components/ui/CcLink";

import { CcCardReadMoreLabel } from "@/components/ui/CcCardReadMoreLabel";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import type { ProjectCardItem } from "@/lib/project-cards.server";
import { useSiteLocale } from "@/components/layout/CcLocaleContext";
import {
  getProjectCardReadLabel,
  getProjectCardReadMoreLabel,
} from "@/lib/cc-cursor-read-labels";
import { getProjectsPageCopy } from "@/lib/projects-page-copy";
import type { Lang } from "@/lib/types";
import { useCcSectionReveal } from "@/lib/use-cc-section-reveal";

const PROJECT_CARD_IMAGE_SIZES =
  "(min-width: 1520px) 464px, (min-width: 1200px) calc((100vw - 128px) / 3), (min-width: 810px) calc((100vw - 96px) / 2), calc(100vw - clamp(48px, 8vw, 64px))";

type CcProjectsGridProps = {
  projects: ProjectCardItem[];
};

export function CcProjectsGrid({ projects }: CcProjectsGridProps) {
  const locale = useSiteLocale();
  const lang = locale as Lang;
  const gridCopy = getProjectsPageCopy(lang).grid;
  const readLabel = getProjectCardReadLabel(locale);
  const readMoreLabel = getProjectCardReadMoreLabel(locale);
  const { ref, withRevealClassName } = useCcSectionReveal({
    staggerChildren: true,
  });

  return (
    <section data-cc-nav="light"
      ref={ref}
      className={withRevealClassName("cc-section cc-projects-grid")}
      aria-labelledby="cc-projects-grid-title"
      {...devSectionAttr("projects-grid")}
    >
      <div className="cc-container cc-projects-grid__container">
        <header className="cc-projects-grid__head">
          <CcSanjayaEyebrow>{gridCopy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-projects-grid-title" className="cc-projects-grid__title">
            {gridCopy.title}
          </h2>
        </header>

        <ul className="cc-projects-grid__list" role="list">
          {projects.map((project) => {
            const { href, tags } = project;

            const titleId = `cc-projects-card-title-${project.id}`;

            return (
              <li key={project.id} className="cc-projects-grid__item">
                <article className="cc-projects-card">
                  <Link
                    href={href}
                    className="cc-projects-card__link"
                    aria-labelledby={titleId}
                  >
                    <div className="cc-projects-card__media">
                      <picture>
                        {project.imageSources?.avif ? <source type="image/avif" srcSet={project.imageSources.avif} sizes={PROJECT_CARD_IMAGE_SIZES} /> : null}
                        {project.imageSources?.webp ? <source type="image/webp" srcSet={project.imageSources.webp} sizes={PROJECT_CARD_IMAGE_SIZES} /> : null}
                        <Image
                          src={project.imageSrc}
                          alt={project.imageAlt}
                          width={960}
                          height={540}
                          className="cc-projects-card__image"
                          sizes={PROJECT_CARD_IMAGE_SIZES}
                          unoptimized={Boolean(project.imageSources?.avif || project.imageSources?.webp)}
                        />
                      </picture>
                      <span className="cc-projects-card__overlay" aria-hidden="true" />
                      <span className="cc-projects-card__overlay-label" aria-hidden="true">
                        {readLabel}
                      </span>
                    </div>
                    <h3 id={titleId} className="cc-projects-card__title">
                      {project.title}
                    </h3>
                    <p className="cc-projects-card__excerpt">{project.excerpt}</p>
                    <CcCardReadMoreLabel className="cc-projects-card__read-more">
                      {readMoreLabel}
                    </CcCardReadMoreLabel>
                  </Link>
                  {tags.length > 0 ? (
                    <ul className="cc-projects-card__tags" aria-label={gridCopy.servicesAria}>
                      {tags.map((tag) => (
                        <li key={tag.tag}>
                          <Link href={tag.href} className="cc-projects-card__tag">
                            {tag.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
